import { HttpClient } from '@zeta-core';
import { APIResponse, IEmptyRequestResponse, IJoinMeetingResponse } from '@zeta-types';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { CLIENT_ADVICE_SERVICE, LLM_SERVICE } from '../services-path';
import {
  AgentNameType,
  businessContextType,
  IActionsResponse,
  IActivityLogDetailResponse,
  IActivityLogSummaryResponse,
  IAddAdhocTaskRequest,
  IAddPlanRequest,
  IAddProductPlanRequest,
  IAgentChatStreamRequest,
  IAgentConversationResponse,
  IAgentResponse,
  IExecutionPlanResponse,
  IExecutionPlanTasks,
  IFactfindClientDocumentResponse,
  IFactfindClientDocumentUploadUrlResponse,
  IFactfindClientSessionResponse,
  IFactfindRequestResponse,
  IFetchAllPlansResponse,
  IGetLatestSummaryResponse,
  IGetRecommendationResponse,
  ILLMChatSessionRequest,
  IPlanTypeResponse,
  IProductSummaryResponse,
  IRecommendationRequest,
  IReorderTasksRequest,
  ISaveFactFindAdviceRequest,
  ISaveFactFindRequest,
} from './types';

export * from './types';

type ClientAdviceLatestSessionResponse = {
  clientId?: string;
  sessionId?: string;
  sessionName?: string;
  contextType?: string;
  status?: string;
  createdAt?: string;
  lastUpdated?: string;
};

type HttpError = {
  response?: AxiosResponse;
};

const toClientAdviceFactfindRequest = (session: ClientAdviceLatestSessionResponse): IFactfindRequestResponse => ({
  factfindRequestId: String(session?.sessionId || ''),
  advisorFirmId: '',
  advisorUserId: '',
  clientId: String(session?.clientId || ''),
  clientType: session?.contextType,
  sessionId: String(session?.sessionId || ''),
  sessionName: session?.sessionName || 'Discovery',
  agentId: '',
  agentName: 'Client Advice',
  serviceType: 'CLIENT_ADVICE',
  factfindMode: 'CLIENT_ADVICE',
  requestStatus: session?.status || 'CURRENT',
  businessContext: 'DISCOVERY',
  requestedDocuments: [],
  createdAt: session?.createdAt,
  lastUpdated: session?.lastUpdated,
});

const fetchClientAdviceLatestSession = (clientId: string): APIResponse<ClientAdviceLatestSessionResponse> =>
  HttpClient.get(`${CLIENT_ADVICE_SERVICE}/journeys/clients/${clientId}/latest-session`);

const isNotFoundResponse = (error: unknown) => (error as HttpError)?.response?.status === 404;

const emptyFactfindRequestsResponse = (error: unknown): AxiosResponse<IFactfindRequestResponse[]> => ({
  ...((error as HttpError).response as AxiosResponse),
  status: 200,
  statusText: 'OK',
  data: [],
});

const FACTFIND_PUBLIC_LLM_SERVICE = (() => {
  if (typeof window === 'undefined') {
    return 'https://zeta-advisor.dev-1.build.dlt/api/v1/llm';
  }

  const { protocol, host } = window.location;
  if (host.includes('zeta-client')) {
    return `${protocol}//${host.replace('zeta-client', 'zeta-advisor')}/api/v1/llm`;
  }

  if (host.includes('localhost')) {
    return 'https://zeta-advisor.dev-1.build.dlt/api/v1/llm';
  }

  return `${protocol}//${host}/api/v1/llm`;
})();

export const LLMService = () => {
  return {
    chatSession: (payload: ILLMChatSessionRequest): APIResponse<{ sessionId: string }> => HttpClient.post(`${LLM_SERVICE}/session/latest`, payload),

    fetchAgent: (payload: { agent_name: AgentNameType }): APIResponse<IAgentResponse> => HttpClient.get(`${LLM_SERVICE}/agents/${payload.agent_name}`),

    fetchAgentList: (): APIResponse<IJoinMeetingResponse> => HttpClient.get(`${LLM_SERVICE}/agents`),

    chatStream: (payload: IAgentChatStreamRequest, config): Promise<Response> => {
      return fetch(`${LLM_SERVICE}/chat/stream/${payload.businessContext}`, config);
    },

    getFactfindClientSession: (token: string): APIResponse<IFactfindClientSessionResponse> =>
      HttpClient.get(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}`, undefined, true),

    getFactfindClientMessages: (token: string): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.get(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/messages`, undefined, true),

    chatFactfindClientStream: (token: string, config): Promise<Response> =>
      fetch(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/chat/stream`, config),

    createFactfindClientDocumentUploadUrl: (
      token: string,
      payload: { fileName: string; contentType: string; documentType?: string; fileSize: number }
    ): APIResponse<IFactfindClientDocumentUploadUrlResponse> =>
      HttpClient.post(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/documents/presign`, payload, undefined, true),

    registerFactfindClientDocument: (
      token: string,
      payload: { documentKey: string; documentName: string; documentType?: string }
    ): APIResponse<IFactfindClientDocumentResponse> =>
      HttpClient.post(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/documents`, payload, undefined, true),

    getFactfindClientDocuments: (token: string): APIResponse<IFactfindClientDocumentResponse[]> =>
      HttpClient.get(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/documents`, undefined, true),

    deleteFactfindClientDocument: (token: string, factfindDocumentId: string): APIResponse<IEmptyRequestResponse> =>
      HttpClient.delete(`${FACTFIND_PUBLIC_LLM_SERVICE}/factfind-client-access/${token}/documents/${factfindDocumentId}`, undefined, true),

    listFactfindRequestsByClient: async (clientId: string): APIResponse<IFactfindRequestResponse[]> => {
      try {
        const response = await fetchClientAdviceLatestSession(clientId);
        return {
          ...response,
          data: response?.data?.sessionId ? [toClientAdviceFactfindRequest(response.data)] : [],
        };
      } catch (error: unknown) {
        if (isNotFoundResponse(error)) {
          return emptyFactfindRequestsResponse(error);
        }
        throw error;
      }
    },

    getSessionByClientAndContext: (clientId: string, context: string): APIResponse<{ sessionId: string }> =>
      HttpClient.get(`${LLM_SERVICE}/session/client/${clientId}/context/${context}`),

    resolveDiscoverySession: async (clientId: string, _clientType?: string): Promise<{ sessionId: string | null }> => {
      try {
        const latestSessionResponse = await fetchClientAdviceLatestSession(clientId);
        return { sessionId: latestSessionResponse?.data?.sessionId || null };
      } catch (error: unknown) {
        if (isNotFoundResponse(error)) {
          return { sessionId: null };
        }
        throw error;
      }
    },

    fetchConversation: (payload: { sessionId: string; businessContext?: businessContextType }): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/session/${payload.sessionId}/messages?businessContext=${payload.businessContext || ''}`),

    getLatestSummary: (payload: { sessionId: string }, signal: AbortSignal | undefined = undefined): APIResponse<IGetLatestSummaryResponse> =>
      HttpClient.get(`${LLM_SERVICE}/summary/${payload.sessionId}/latest`, { signal }),

    getAllVersions: (payload: { sessionId: string }): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/summary/${payload.sessionId}/versions`),

    generateFactFindSummary: (payload: { sessionId: string; agentMode: string }): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/session/${payload.sessionId}/${payload.agentMode}/summarise`),

    getRestoreVersion: (payload: { version: number; sessionId: string; versionId: string }): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/summary/${payload.sessionId}/summary/versions/${payload.versionId}`),

    restoreReport: (payload: { sessionId: string; version: number; versionId: string }): APIResponse<IAgentConversationResponse[]> =>
      HttpClient.post(`${LLM_SERVICE}/summary/restore`, payload),

    getRiskAssessment: (payload: { sessionId: string }): APIResponse<any> => HttpClient.get(`${LLM_SERVICE}/risk/${payload.sessionId}/get`),

    getGoals: (payload: { sessionId: string }): APIResponse<any> => HttpClient.get(`${LLM_SERVICE}/goals/${payload.sessionId}/get`),

    validateGoalName: (payload: { sessionId: string; goalName: string }): APIResponse<any> =>
      HttpClient.get(`${LLM_SERVICE}/goals/${payload.sessionId}/valid-goal-name/${payload.goalName}`),

    saveFactFind: (payload: ISaveFactFindRequest): APIResponse<any> => HttpClient.post(`${LLM_SERVICE}/summary/update`, payload),

    saveFactFindAdvice: (payload: ISaveFactFindAdviceRequest): APIResponse<any> => HttpClient.post(`${LLM_SERVICE}/summary/update/advice`, payload),

    saveRecommendation: (payload: IRecommendationRequest): APIResponse<any> => HttpClient.post(`${LLM_SERVICE}/summary/recommendation/plans`, payload),

    getRecommendations: (payload: { summaryId: string }): APIResponse<IGetRecommendationResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/summary/recommendation/${payload.summaryId}/all`),

    refreshRecommendations: (payload: { recommendationId: string }): APIResponse<any> =>
      HttpClient.get(`${LLM_SERVICE}/summary/recommendation/${payload.recommendationId}/refresh`),

    getSelectedRecommendation: (payload: { recommendationId: string }): APIResponse<any> =>
      HttpClient.get(`${LLM_SERVICE}/summary/recommendation/${payload.recommendationId}/fetch`),

    getRecommendationImplementation: (payload: { recommendationId: string }): APIResponse<{ implementation: string }> =>
      HttpClient.get(`${LLM_SERVICE}/summary/recommendation/${payload.recommendationId}/implementation`),

    getRecommendationReview: (payload: { recommendationId: string }): APIResponse<{ review: string }> =>
      HttpClient.get(`${LLM_SERVICE}/summary/recommendation/${payload.recommendationId}/review`),

    AddPlan: (payload: IAddPlanRequest): APIResponse<IEmptyRequestResponse> => HttpClient.post(`${LLM_SERVICE}/plans`, payload),

    fetchRecommendationPlans: (payload: { sessionId: string }): APIResponse<IFetchAllPlansResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/plans/${payload.sessionId}`),

    fetchPlanTypes: (): APIResponse<IPlanTypeResponse> => HttpClient.get(`${LLM_SERVICE}/plans/planTypes`),

    markAsComplete: (payload: { planId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/plans/${payload.planId}/action/markAsComplete`),

    triggerReport: (payload: { planId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${LLM_SERVICE}/plans/${payload.planId}/action/trigger-report`),

    updateSummary: (payload: { planId: string; summary: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/plans/${payload.planId}/action/updateSummary`, payload),

    refreshRecommendationPlan: (payload: { planId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${LLM_SERVICE}/plans/${payload.planId}/refresh`),

    regenerateRecommendationPlan: (payload: { planId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${LLM_SERVICE}/plans/${payload.planId}/report/action/regenerate`),

    fetchActions: (clientId: string): APIResponse<IActionsResponse[]> => HttpClient.get(`${LLM_SERVICE}/actions/${clientId}/fetch`),

    getExecutionPlans: (payload: { clientID: string; status: string }): APIResponse<IExecutionPlanResponse> =>
      HttpClient.get(`${LLM_SERVICE}/execution-plan/clients/${payload.clientID}/plans?status=${payload.status || ''}`),

    addAdhocTask: (payload: IAddAdhocTaskRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${LLM_SERVICE}/execution-plan/${payload.planId}/adhoc-tasks`, payload),

    reorderTasks: (payload: IReorderTasksRequest): APIResponse<IEmptyRequestResponse> => HttpClient.put(`${LLM_SERVICE}/execution-plan/tasks/reorder`, payload),

    deleteTask: (payload: { taskId: string }): APIResponse<IEmptyRequestResponse> => HttpClient.delete(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}`),

    getTaskDocuments: (payload: { taskId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.get(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}/documents`),
    getDocumentByID: (payload: { taskId: string; documentId: string }, config: AxiosRequestConfig): APIResponse<IEmptyRequestResponse> =>
      HttpClient.download(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}/documents/${payload.documentId}`, {}, config),

    deleteTaskDocument: (payload: { taskId: string; documentId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.delete(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}/documents/${payload.documentId}`),

    uploadDocument: (taskId: string, payload: File, config: any): APIResponse<{ documentId: string }> =>
      HttpClient.upload(`${LLM_SERVICE}/execution-plan/tasks/${taskId}/documents`, payload, config),

    updateTask: (payload: { taskId: string; markComplete: boolean; notes: string }, taskId: string): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/execution-plan/tasks/${taskId}`, payload),

    getTaskDetails: (payload: { taskId: string }): APIResponse<IExecutionPlanTasks> => HttpClient.get(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}`),

    addAssignedToTask: (payload: { taskId: string; assigneeId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}/assignee`, payload),

    getStartedTask: (payload: { taskId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/execution-plan/tasks/${payload.taskId}/get-started`),

    getProductSummary: (payload: { planId: string }): APIResponse<IProductSummaryResponse[]> =>
      HttpClient.get(`${LLM_SERVICE}/plans/${payload.planId}/products`),

    getProductSolutionTypes: (payload: { planType: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.get(`${LLM_SERVICE}/plans/${payload.planType}/product/types`),

    addProduct: (payload: IAddProductPlanRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${LLM_SERVICE}/plans/${payload.planId}/products`, payload),

    updateProduct: (payload: IAddProductPlanRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.put(`${LLM_SERVICE}/plans/${payload.planId}/products/${payload.productId}`, payload),

    deleteProduct: (payload: { planId: string; productId: string }): APIResponse<IEmptyRequestResponse> =>
      HttpClient.delete(`${LLM_SERVICE}/plans/${payload.planId}/products/${payload.productId}`),
  };
};
